import { Fragment, useState, type ReactElement } from 'react'
import { Link } from 'react-router'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { LogOut, MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'tailwind-v4/components/dropdown-menu'
import { Button } from 'tailwind-v4/components/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import useUser from '@/hooks/useUser'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import { AccountAvatar } from './account-avatar'
import { useAccountMenu, type AccountMenuItem } from './use-account-menu'

const iconBtn =
  'flex size-7 flex-none items-center justify-center rounded-md text-[var(--muted-foreground)] transition-colors hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent-foreground)]'

const BUILD_TIME_LABEL = format(
  __DATE__,
  TimeFormats.StandardDateMonthYearSmallHHmm
)
const BUILD_SUMMARY = `Build ${__COMMIT_SHA__} · ${BUILD_TIME_LABEL} · ${__APP_ENV__ ?? 'development'}`

/**
 * Quiet build-identity row above the user footer — THE "what build are you
 * on?" answer when debugging with a customer. Click opens a dialog with the
 * full detail and a copy action.
 */
function BuildInfoRow() {
  const [open, setOpen] = useState(false)

  const rows = [
    { label: 'Commit', value: __COMMIT_SHA__, mono: true },
    { label: 'Built', value: BUILD_TIME_LABEL, mono: false },
    { label: 'Environment', value: __APP_ENV__ ?? 'development', mono: true },
  ]

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => {
          setOpen(true)
        }}
        title={BUILD_SUMMARY}
        className="w-full truncate px-2 py-1 text-left text-[11px] whitespace-nowrap text-[var(--muted-foreground)] transition-colors hover:text-[var(--sidebar-foreground)]"
      >
        Build time: {BUILD_TIME_LABEL}
      </button>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>About this build</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Share this with support when reporting an issue.
          </p>
        </DialogHeader>
        <div className="flex flex-col divide-y divide-border">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-baseline justify-between gap-4 py-2"
            >
              <span className="text-sm text-muted-foreground">{row.label}</span>
              <span
                className={
                  row.mono
                    ? 'min-w-0 font-mono text-sm break-all text-foreground'
                    : 'min-w-0 text-sm text-foreground'
                }
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button
            variant="primary"
            onClick={() => {
              void navigator.clipboard.writeText(BUILD_SUMMARY)
              toast.success('Build info copied')
            }}
          >
            Copy build info
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Sidebar footer user row: gravatar + first name, a three-dot menu mirroring
 * the account options (the same links the old account menu gave) plus the
 * UI toggle + support, and a direct logout button. Shared by both UIs.
 */
export function SidebarUserFooter({ orgId }: { orgId: string }) {
  const user = useUser()
  const { newUi } = useFeatureFlags()
  const { sections, dialogs } = useAccountMenu(orgId)

  const renderAccountItem = (item: AccountMenuItem): ReactElement => {
    const Icon = item.icon
    const label = <span>{item.label}</span>
    const isDestructive = item.tone === 'destructive'
    const iconClass = isDestructive ? undefined : 'text-[var(--muted-foreground)]'
    const variant = isDestructive ? ('destructive' as const) : undefined

    if (item.kind === 'action') {
      return (
        <DropdownMenuItem key={item.id} variant={variant} onClick={item.onSelect}>
          <Icon className={iconClass} />
          {label}
        </DropdownMenuItem>
      )
    }

    if (item.kind === 'external') {
      return (
        <DropdownMenuItem
          key={item.id}
          variant={variant}
          render={
            <a
              href={item.href}
              target="_blank"
              rel="noreferrer"
              aria-label={item.label}
            />
          }
        >
          <Icon className={iconClass} />
          {label}
        </DropdownMenuItem>
      )
    }

    return (
      <DropdownMenuItem
        key={item.id}
        variant={variant}
        render={<Link to={item.to} />}
      >
        <Icon className={iconClass} />
        {label}
      </DropdownMenuItem>
    )
  }

  return (
    <>
      {dialogs}
      <BuildInfoRow />
      <div className="flex items-center gap-1.5 p-1">
        <AccountAvatar
          first={user?.first}
          last={user?.last}
          email={user?.email}
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--sidebar-foreground)]">
          {user?.first ?? 'Account'}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger className={iconBtn} aria-label="Account options">
            <MoreHorizontal className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="end"
            className={newUi.enabled ? 'w-56' : 'old-ui-nav w-56'}
          >
            <div className="px-1.5 py-1">
              <div className="truncate text-sm font-medium text-[var(--popover-foreground)]">
                {user?.first} {user?.last}
              </div>
              <div className="truncate text-xs font-normal text-[var(--muted-foreground)]">
                {user?.email}
              </div>
            </div>
            {sections.map((section) => (
              <Fragment key={section.id}>
                <DropdownMenuSeparator />
                {section.items.map(renderAccountItem)}
              </Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Link to="/oauth/logout" aria-label="Log out" className={iconBtn}>
          <LogOut className="size-4" />
        </Link>
      </div>
    </>
  )
}
